<template>
  <span v-if="renderCell">
    <template v-if="renderCellType">
      <template v-if="renderCellType.type === 'tag'">
        <template v-if="renderCellType.cell && hasCellValue">
          <template v-if="renderCellType.cell.separator">
            <el-tag v-for="(tag, index) in cellValue.split(new RegExp(renderCellType.cell.separator))"
                    :key="'table-cell-' + index"
                    :type="tableCellThemeType">
              <span v-html="getRenderCellValue(rowData, tableColumn.prop, tag)"></span>
            </el-tag>
          </template>
          <el-tag v-else
                  :type="tableCellThemeType">
            <span v-html="renderCellValue"></span>
          </el-tag>
        </template>
        <template v-else>
          <span v-if="hasCellValue"
                :class="'grid-cell-' + tableCellThemeType"
                v-html="renderCellValue"></span>
          <span v-else
                class="grid-cell-empty">{{renderCell.emptyText}}</span>
        </template>
      </template>
      <template v-else-if="renderCellType.type === 'switch'">
        <template v-if="renderCellType.cell && hasCellValue">
          <el-switch v-model="formCellData"
                     :disabled="!renderCellType.cell.requestPath"
                     :active-value="renderCellType.cell.activeValue"
                     :inactive-value="renderCellType.cell.activeValue === cellValue ? (renderCellType.cell.inactiveValue || false) : cellValue"
                     @change="(val) => { handleDataChange(rowData, renderCellType.cell.requestPath, val) }"></el-switch>
        </template>
        <template v-else>
          <span v-if="hasCellValue"
                :class="'grid-cell-' + tableCellThemeType"
                v-html="renderCellValue"></span>
          <span v-else
                class="grid-cell-empty">{{renderCell.emptyText}}</span>
        </template>
      </template>
      <template v-else-if="renderCellType.type === 'rate'">
        <template v-if="renderCellType.cell && hasCellValue">
          <el-rate v-model="formCellData"
                   :disabled="!renderCellType.cell.requestPath"
                   :max="renderCellType.cell.maxValue"
                   show-score
                   allow-half
                   @change="(val) => { handleDataChange(rowData, renderCellType.cell.requestPath, val) }"></el-rate>
        </template>
        <template v-else>
          <span v-if="hasCellValue"
                :class="'grid-cell-' + tableCellThemeType"
                v-html="renderCellValue"></span>
          <span v-else
                class="grid-cell-empty">{{renderCell.emptyText}}</span>
        </template>
      </template>
      <template v-else-if="renderCellType.type === 'progress'">
        <template v-if="renderCellType.cell && hasCellValue">
          <el-progress :type="renderCellType.cell.type"
                       :text-inside="renderCellType.cell.type === 'line'"
                       :stroke-width="renderCellType.cell.type === 'line' ? 12 : 2"
                       :width="40"
                       :percentage="Math.round(cellValue * 100 / (renderCellType.cell.maxValue || 100))"
                       :class="'progress-bar-' + tableCellThemeType"></el-progress>
        </template>
        <template v-else>
          <span v-if="hasCellValue"
                :class="'grid-cell-' + tableCellThemeType"
                v-html="renderCellValue"></span>
          <span v-else
                class="grid-cell-empty">{{renderCell.emptyText}}</span>
        </template>
      </template>
      <template v-else-if="renderCellType.type === 'custom'">
        <template v-if="renderCellType.cell && (hasCellValue || renderCellType.cell.acceptEmpty)">
          <Tile :class="'grid-cell-' + tableCellThemeType"
                :component-path="renderCellType.cell.component"
                :cell-param="renderCellType.cell.cellParam"
                :table-column="tableColumn"
                :table-column-cell-render="tableColumnCellRender"
                :row-index="rowIndex"
                :row-data="rowData"
                :cell-value="cellValue"
                :empty-text="renderCell.emptyText"
                v-bind="$attrs"
                @reloadGridData="reloadGridData"></Tile>
        </template>
        <template v-else>
          <span v-if="hasCellValue"
                :class="'grid-cell-' + tableCellThemeType"
                v-html="renderCellValue"></span>
          <span v-else
                class="grid-cell-empty">{{renderCell.emptyText}}</span>
        </template>
      </template>
      <template v-else>
        <span v-if="hasCellValue"
              :class="'grid-cell-' + tableCellThemeType"
              v-html="renderCellValue"></span>
        <span v-else
              class="grid-cell-empty">{{renderCell.emptyText}}</span>
      </template>
    </template>
    <template v-else>
      <span v-if="hasCellValue"
            :class="'grid-cell-' + tableCellThemeType"
            v-html="renderCellValue"></span>
      <span v-else
            class="grid-cell-empty">{{renderCell.emptyText}}</span>
    </template>
  </span>
  <span v-else v-html="renderCellValue"></span>
</template>

<script>
import { getValueByPath } from 'element-ui/lib/utils/util'
import ColumnExtra from '../../../mixins/grid/columnExtra'
import Tile from '../Tile'
import objectUtils from '../../../util/objectUtils'
import routeConfig from '../../../constants/routeConfig'
import globalEvent from '../../../constants/globalEvent'
export default {
  name: 'CompositeTableRenderCell',
  mixins: [ColumnExtra],
  components: {Tile},
  props: {
    tableColumn: {
      type: Object,
      required: true
    },
    rowIndex: {
      type: Number,
      required: true
    },
    rowData: {
      type: Object,
      required: true
    },
    tableColumnCellRender: {
      type: Function,
      required: true
    }
  },
  provide () {
    return {
      $cell: this
    }
  },
  data () {
    return {
      formCellData: null
    }
  },
  computed: {
    renderCell: function () {
      return this.tableColumn['renderCell']
    },
    renderCellType: function () {
      return this.renderCell ? this.renderCell.cellType : null
    },
    cellValue: function () {
      return getValueByPath(this.rowData, this.tableColumn.prop)
    },
    hasCellValue: function () {
      return objectUtils.hasValue(this.cellValue)
    },
    tableCellThemeType: function () {
      let renderCell = this.renderCell
      if (renderCell) {
        let cellMatcher = renderCell['cellMatcher']
        if (cellMatcher) {
          let cellThemeType
          cellMatcher.some((cellMatcherItem) => {
            let columnMatcher = cellMatcherItem['columnMatcher']
            if (columnMatcher) {
              for (let index = 0; index < columnMatcher.length; index++) {
                let columnMatcherItem = columnMatcher[index]
                let cellValue = getValueByPath(this.rowData, columnMatcherItem.prop)
                // 必须是全匹配
                let cellRegex = new RegExp('^' + columnMatcherItem.regex + '$')
                if (cellRegex.test(objectUtils.toString(cellValue))) {
                  if (index === columnMatcher.length - 1) {
                    cellThemeType = cellMatcherItem['themeType']
                  }
                } else {
                  break
                }
              }
            }
          })
          if (cellThemeType) {
            return cellThemeType
          }
        }
        return renderCell['themeType']
      }
      return ''
    },
    renderCellValue: function () {
      return this.getRenderCellValue(this.rowData, this.tableColumn.prop, getValueByPath(this.rowData, this.tableColumn.prop))
    }
  },
  methods: {
    getRenderCellValue (row, prop, value) {
      if (this.is__hidden) {
        return value
      } else {
        return this.tableColumnCellRender(row, prop, value)
      }
    },
    // 重新加载数据
    reloadGridData () {
      this.$emit('reloadGridData')
    },
    handleDataChange (row, url, val) {
      let postParams = this.$extend(this.$attrs)
      if (!postParams[routeConfig.DataGrid.param]) {
        return
      }
      let params = this.$extend({
        postId: objectUtils.toString(getValueByPath(row, 'id')),
        postData: val
      }, postParams)
      this.$ajax({
        url: url,
        method: 'post',
        data: this.$qs.stringify(params, {
          arrayFormat: 'repeat'
        })
      }).then((response) => {
        if (response.data) {
          switch (response.data['responseCode']) {
            case 'SUCCESS': {
              this.$message({
                message: response.data['message'],
                type: 'success',
                center: true
              })
              this.$bus.$emit(`${globalEvent.templateGrid.DATA_STATE_CHANGED}:${this.$attrs[routeConfig.DataGrid.param]}`)
              break
            }
            default: {
              this.$message({
                message: response.data['message'],
                type: 'warning',
                center: true
              })
              break
            }
          }
        }
      }, () => {
        this.$message.error({
          message: '请求发送失败，请稍候重试',
          center: true
        })
      }).finally(() => {
        // 操作成功或失败都需刷新列表
        this.reloadGridData()
      })
    }
  },
  created () {
    this.formCellData = this.cellValue
  },
  watch: {
    rowData: function () {
      this.formCellData = this.cellValue
    }
  }
}
</script>
